/**
 * 用新 URL 复制一个 `Request` 对象，其余字段（method/headers/body/credentials/signal 等）保持一致。
 *
 * # 为什么需要这个 helper
 *
 * `Request.url` 是 readonly（Web Fetch 规范规定），想改 URL 必须新建 Request。
 *
 * # 为什么必须穷举字段而不能 `{ ...req, body }`
 *
 * `Request` 实例的所有字段都是原型链上的 **getter**（不是 own enumerable property），
 * 因此 `{ ...req }` 和 `Object.keys(req)` 都返回空对象/空数组：
 *
 *   const r = new Request('https://example.com/', { method: 'POST' });
 *   Object.keys(r);     // []
 *   { ...r };           // {}
 *
 * Web Platform 规范故意把 Request 设计成 immutable + opaque，避免开发者把它当 plain object
 * 操作时漏掉浏览器引擎里的 native state（cache key / CORS 上下文 / stream descriptor 等）。
 * 代价是 clone 时只能挨个访问 getter 把字段读出来。
 *
 * # 字段穷举的完整性
 *
 * 已覆盖 `RequestInit` 标准里所有"能从 Request 实例读到的"字段（method/headers/body/mode/
 * credentials/cache/redirect/referrer/referrerPolicy/integrity/keepalive/signal）。
 * 不拷贝 `priority` 是因为 Request 实例不暴露这个 getter（lib.dom 也没收录），任何 clone
 * 写法都无法保留它；不拷贝 `window` 是因为它只能传 null 用于 disassociate，正常调用无意义。
 *
 * # 跨浏览器兼容
 *
 * 1. 不能用 `new Request(newUrl, originalRequest)` 这种把 Request 当 init 用的简写：
 *    Safari 严格模式抛 `TypeError`（Chrome/Firefox 容错支持）。
 * 2. 不能直接传 `body: req.body`（ReadableStream）：
 *    Safari 抛 `NotSupportedError: ReadableStream uploading is not supported`，
 *    Chrome/Firefox 也要求显式 `duplex: 'half'`。
 *
 * 解法：用 `arrayBuffer()` 把 body 读成定长二进制再传，跨浏览器一致。
 *
 * # 用法
 *
 *   const next = await cloneRequestWithUrl(req, 'https://example.com/new-path');
 *
 * # 注意
 *
 * 调用后原 `req.body` 已被消费，不能再次读取。绝大多数 interceptor 场景里 req 改写后即丢弃，无影响。
 */
export const cloneRequestWithUrl = async (req: Request, newUrl: string): Promise<Request> => {
  const body = req.method === 'GET' || req.method === 'HEAD' ? undefined : await req.arrayBuffer();
  return new Request(newUrl, {
    method: req.method,
    headers: req.headers,
    body,
    mode: req.mode,
    credentials: req.credentials,
    cache: req.cache,
    redirect: req.redirect,
    referrer: req.referrer,
    referrerPolicy: req.referrerPolicy,
    integrity: req.integrity,
    keepalive: req.keepalive,
    signal: req.signal,
  });
};
